import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { StructureCastClient } from "serviceStores/mock/structurecast-clients.mock";
import type { StructureCastProject } from "serviceStores/mock/structurecast-projects.mock";
import type { StructureCastStructure } from "serviceStores/mock/structurecast-structures.mock";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyStore = any;

class StructureCastJobsClientsStore {
  rootStore: RootStore;

  // UI state
  currentTab = 0;
  searchQuery = "";
  statusFilter: string = "all";
  selectedClientId: string | null = null;

  // Dialog state
  editingClient: StructureCastClient | null = null;
  editingProject: StructureCastProject | null = null;

  // Loading state
  loading = false;
  error: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // --- Mock store accessors ---

  private get _clientStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastClientsStore;
  }

  private get _projectStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastProjectsStore;
  }

  private get _structureStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastStructuresStore;
  }

  // --- Computed: raw entity lists ---

  get clients(): StructureCastClient[] {
    return (this._clientStore?.entities as StructureCastClient[]) || [];
  }

  get projects(): StructureCastProject[] {
    return (this._projectStore?.entities as StructureCastProject[]) || [];
  }

  get structures(): StructureCastStructure[] {
    return (this._structureStore?.entities as StructureCastStructure[]) || [];
  }

  // --- Computed: filtered lists ---

  get filteredClients(): StructureCastClient[] {
    let result = this.clients;
    if (this.statusFilter !== "all") {
      result = result.filter((c) => c.status === this.statusFilter);
    }
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.clientCode.toLowerCase().includes(query) ||
          c.clientName.toLowerCase().includes(query) ||
          c.contactName.toLowerCase().includes(query) ||
          c.city.toLowerCase().includes(query)
      );
    }
    return result;
  }

  get filteredProjects(): StructureCastProject[] {
    let result = this.projects;
    if (this.selectedClientId) {
      result = result.filter((p) => p.clientId === this.selectedClientId);
    }
    if (this.statusFilter !== "all") {
      result = result.filter((p) => p.status === this.statusFilter);
    }
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.projectCode.toLowerCase().includes(query) ||
          p.projectName.toLowerCase().includes(query) ||
          p.location.toLowerCase().includes(query) ||
          p.stationReference.toLowerCase().includes(query) ||
          p.projectManager.toLowerCase().includes(query)
      );
    }
    return result;
  }

  // --- Helpers ---

  getProjectCountForClient = (clientId: string): number => {
    return this.projects.filter((p) => p.clientId === clientId).length;
  };

  getStructureCountForProject = (projectId: string): number => {
    return this.structures.filter((s) => s.projectId === projectId).length;
  };

  getClientName = (clientId: string): string => {
    const client = this.clients.find((c) => c.id === clientId);
    return client?.clientName || "Unknown";
  };

  // --- Summary metrics ---

  get totalClients(): number {
    return this.clients.length;
  }

  get activeClients(): number {
    return this.clients.filter((c) => c.status === "active").length;
  }

  get totalProjects(): number {
    return this.projects.length;
  }

  get activeProjects(): number {
    return this.projects.filter((p) => p.status === "active").length;
  }

  // --- Actions ---

  setCurrentTab = (tab: number) => {
    this.currentTab = tab;
  };

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  setStatusFilter = (status: string) => {
    this.statusFilter = status;
  };

  setSelectedClientId = (clientId: string | null) => {
    this.selectedClientId = clientId;
  };

  viewClientProjects = (clientId: string) => {
    this.selectedClientId = clientId;
    this.currentTab = 1;
  };

  clearClientFilter = () => {
    this.selectedClientId = null;
  };

  // --- CRUD: Clients ---

  createClient = async (data: Omit<StructureCastClient, "id" | "createdAt" | "updatedAt">) => {
    const now = new Date().toISOString();
    const newClient = {
      ...data,
      id: `sc-client-new-${Date.now()}`,
      createdAt: now,
      updatedAt: now,
    };
    if (this._clientStore) {
      await this._clientStore.create(newClient);
    }
    this.editingClient = null;
  };

  updateClient = async (clientId: string, data: Partial<StructureCastClient>) => {
    if (this._clientStore) {
      await this._clientStore.patch(clientId, {
        ...data,
        updatedAt: new Date().toISOString(),
      });
    }
    this.editingClient = null;
  };

  removeClient = async (clientId: string) => {
    if (this._clientStore) {
      await this._clientStore.remove(clientId);
    }
  };

  // --- CRUD: Projects ---

  createProject = async (data: Omit<StructureCastProject, "id" | "createdAt" | "updatedAt">) => {
    const now = new Date().toISOString();
    const newProject = {
      ...data,
      id: `sc-proj-new-${Date.now()}`,
      createdAt: now,
      updatedAt: now,
    };
    if (this._projectStore) {
      await this._projectStore.create(newProject);
    }
    this.editingProject = null;
  };

  updateProject = async (projectId: string, data: Partial<StructureCastProject>) => {
    if (this._projectStore) {
      await this._projectStore.patch(projectId, {
        ...data,
        updatedAt: new Date().toISOString(),
      });
    }
    this.editingProject = null;
  };

  removeProject = async (projectId: string) => {
    if (this._projectStore) {
      await this._projectStore.remove(projectId);
    }
  };

  // --- Dialog helpers ---

  openClientDialog = (client?: StructureCastClient) => {
    this.editingClient = client || null;
    this.rootStore.routerStore.openDialog({
      dialogName: "clientForm",
    });
  };

  openProjectDialog = (project?: StructureCastProject) => {
    this.editingProject = project || null;
    this.rootStore.routerStore.openDialog({
      dialogName: "projectForm",
    });
  };

  // --- Load data ---

  loadData = async () => {
    this.loading = true;
    this.error = null;
    try {
      await Promise.all([
        this._clientStore?.find(),
        this._projectStore?.find(),
        this._structureStore?.find(),
      ]);
      runInAction(() => {
        this.loading = false;
      });
    } catch (err: unknown) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to load data";
        this.loading = false;
      });
    }
  };
}

export default StructureCastJobsClientsStore;
